import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Form, Modal, Badge, Nav } from 'react-bootstrap';
import { Plus, Search, BookmarkStar, Eye, Code } from 'react-bootstrap-icons';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const ResearchNotes = ({ activeProfileId }) => {
    const [researchNotes, setResearchNotes] = useState([]);
    const [showResearchModal, setShowResearchModal] = useState(false);
    const [showBlogView, setShowBlogView] = useState(false);
    const [selectedResearch, setSelectedResearch] = useState(null);
    const [viewingResearch, setViewingResearch] = useState(null);
    const [previewMode, setPreviewMode] = useState(false);
    const [formData, setFormData] = useState({
        topic: '',
        research_question: '',
        content: '',
        sources: '',
        tags: '',
        status: 'In Progress'
    });

    const statusOptions = ['In Progress', 'Completed', 'On Hold', 'Archived'];
    const topicCategories = ['Technology', 'Business', 'Science', 'Health', 'Education', 'Finance', 'Other'];

    useEffect(() => {
        if (activeProfileId) {
            fetchResearchNotes();
        }
    }, [activeProfileId]);

    const fetchResearchNotes = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/research-notes`);
            setResearchNotes(response.data);
        } catch (error) {
            console.error('Error fetching research notes:', error);
        }
    };

    const handleEditResearch = (note) => {
        setSelectedResearch(note);
        setFormData({
            topic: note.topic || '',
            research_question: note.research_question || '',
            content: note.content || '',
            sources: note.sources || '',
            tags: note.tags || '',
            status: note.status || 'In Progress'
        });
        setShowResearchModal(true);
    };

    const handleAddResearch = () => {
        setSelectedResearch(null);
        setFormData({
            topic: '',
            research_question: '',
            content: '',
            sources: '',
            tags: '',
            status: 'In Progress'
        });
        setShowResearchModal(true);
    };

    const handleViewResearch = (note) => {
        setViewingResearch(note);
        setShowBlogView(true);
    };

    const handleSubmitResearch = async (e) => {
        e.preventDefault();
        const formDataToSend = new FormData(e.target);
        
        try {
            if (selectedResearch) {
                await axios.put(`${API_BASE_URL}/api/life/research-notes/${selectedResearch.id}`, formDataToSend);
            } else {
                await axios.post(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/research-notes`, formDataToSend);
            }
            fetchResearchNotes();
            setShowResearchModal(false);
            setSelectedResearch(null);
            e.target.reset();
        } catch (error) {
            console.error('Error saving research note:', error);
        }
    };

    const handleDeleteResearch = async (noteId) => {
        if (window.confirm('Are you sure you want to delete this research note?')) {
            try {
                await axios.delete(`${API_BASE_URL}/api/life/research-notes/${noteId}`);
                fetchResearchNotes();
            } catch (error) {
                console.error('Error deleting research note:', error);
            }
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Completed': return 'success';
            case 'In Progress': return 'primary';
            case 'On Hold': return 'warning';
            case 'Archived': return 'secondary';
            default: return 'primary';
        }
    };

    return (
        <div className="research-notes-container">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h5><Search className="me-2" />Research Notes</h5>
                <Button variant="primary" onClick={handleAddResearch}>
                    <Plus /> Add Research
                </Button>
            </div>
            
            {researchNotes.length === 0 ? (
                <Card className="text-center p-4">
                    <p className="text-muted">No research notes yet. Start documenting your research topics!</p>
                </Card>
            ) : (
                <Row>
                    {researchNotes.map(note => (
                        <Col md={6} lg={4} key={note.id} className="mb-3">
                            <Card className="h-100" style={{ cursor: 'pointer' }} onClick={() => handleViewResearch(note)}>
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <h6 className="card-title">{note.topic}</h6>
                                        <Badge bg={getStatusColor(note.status)}>{note.status}</Badge>
                                    </div>
                                    {note.research_question && (
                                        <p className="text-muted mb-2">
                                            <strong>Question:</strong> {note.research_question}
                                        </p>
                                    )}
                                    {note.content && (
                                        <div className="card-text" style={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden'
                                        }}>
                                            <ReactMarkdown>{note.content}</ReactMarkdown>
                                        </div>
                                    )}
                                    {note.tags && (
                                        <div className="mb-2">
                                            {note.tags.split(',').map((tag, index) => (
                                                <Badge key={index} bg="light" text="dark" className="me-1">
                                                    #{tag.trim()}
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                    <small className="text-muted">
                                        {new Date(note.updated_at).toLocaleDateString()}
                                    </small>
                                </Card.Body>
                                <Card.Footer className="bg-transparent">
                                    <div className="d-flex justify-content-end gap-2">
                                        <Button variant="outline-primary" size="sm" onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditResearch(note);
                                        }}>
                                            Edit
                                        </Button>
                                        <Button variant="outline-danger" size="sm" onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteResearch(note.id);
                                        }}>
                                            Delete
                                        </Button>
                                    </div>
                                </Card.Footer>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {/* Add/Edit Research Modal */}
            <Modal show={showResearchModal} onHide={() => {setShowResearchModal(false); setSelectedResearch(null);}} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{selectedResearch ? 'Edit Research Note' : 'Add Research Note'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmitResearch}>
                        <Form.Group className="mb-3">
                            <Form.Label>Topic *</Form.Label>
                            <Form.Control 
                                name="topic" 
                                value={formData.topic}
                                onChange={(e) => setFormData({...formData, topic: e.target.value})}
                                required 
                                placeholder="e.g., Machine Learning Algorithms, Market Analysis" 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Research Question</Form.Label>
                            <Form.Control 
                                name="research_question" 
                                value={formData.research_question}
                                onChange={(e) => setFormData({...formData, research_question: e.target.value})}
                                placeholder="What specific question are you trying to answer?" 
                            />
                        </Form.Group>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select 
                                        name="status" 
                                        value={formData.status}
                                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                                    >
                                        {statusOptions.map(status => (
                                            <option key={status} value={status}>{status}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Tags</Form.Label>
                                    <Form.Control 
                                        name="tags" 
                                        value={formData.tags}
                                        onChange={(e) => setFormData({...formData, tags: e.target.value})}
                                        placeholder="Comma-separated tags" 
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label>Research Content</Form.Label>
                                <Nav variant="pills" className="nav-sm">
                                    <Nav.Item>
                                        <Nav.Link 
                                            active={!previewMode} 
                                            onClick={() => setPreviewMode(false)}
                                            className="py-1 px-2"
                                        >
                                            <Code size={14} /> Edit
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link 
                                            active={previewMode} 
                                            onClick={() => setPreviewMode(true)}
                                            className="py-1 px-2"
                                        >
                                            <Eye size={14} /> Preview
                                        </Nav.Link>
                                    </Nav.Item>
                                </Nav>
                            </div>
                            {previewMode ? (
                                <div 
                                    style={{ 
                                        border: '1px solid #ced4da', 
                                        borderRadius: '0.375rem', 
                                        padding: '0.75rem',
                                        minHeight: '150px',
                                        backgroundColor: '#f8f9fa'
                                    }}
                                >
                                    <ReactMarkdown>{formData.content || '*No content to preview*'}</ReactMarkdown>
                                </div>
                            ) : (
                                <Form.Control 
                                    name="content" 
                                    as="textarea" 
                                    rows={6} 
                                    value={formData.content}
                                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                                    placeholder="Your research findings, notes, and insights... (Supports Markdown)" 
                                />
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Sources</Form.Label>
                            <Form.Control 
                                name="sources" 
                                as="textarea" 
                                rows={3} 
                                value={formData.sources}
                                onChange={(e) => setFormData({...formData, sources: e.target.value})}
                                placeholder="URLs, books, papers, and other references..." 
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={() => {setShowResearchModal(false); setSelectedResearch(null);}}>Cancel</Button>
                            <Button variant="primary" type="submit">{selectedResearch ? 'Update Research' : 'Add Research'}</Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Blog View Modal */}
            <Modal show={showBlogView} onHide={() => setShowBlogView(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>{viewingResearch?.topic}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {viewingResearch && (
                        <div className="blog-content">
                            <div className="mb-3">
                                <Badge bg={getStatusColor(viewingResearch.status)} className="me-2">
                                    {viewingResearch.status}
                                </Badge>
                                <small className="text-muted">
                                    Last updated: {new Date(viewingResearch.updated_at).toLocaleDateString()}
                                </small>
                            </div>
                            
                            {viewingResearch.research_question && (
                                <div className="mb-4">
                                    <h6>Research Question</h6>
                                    <p className="text-muted">{viewingResearch.research_question}</p>
                                </div>
                            )}
                            
                            {viewingResearch.content && (
                                <div className="mb-4">
                                    <div className="markdown-content">
                                        <ReactMarkdown>{viewingResearch.content}</ReactMarkdown>
                                    </div>
                                </div>
                            )}
                            
                            {viewingResearch.sources && (
                                <div className="mb-4">
                                    <h6>Sources & References</h6>
                                    <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                                        {viewingResearch.sources}
                                    </div>
                                </div>
                            )}
                            
                            {viewingResearch.tags && (
                                <div className="mb-3">
                                    <h6>Tags</h6>
                                    {viewingResearch.tags.split(',').map((tag, index) => (
                                        <Badge key={index} bg="light" text="dark" className="me-1">
                                            #{tag.trim()}
                                        </Badge>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => {
                        setShowBlogView(false);
                        handleEditResearch(viewingResearch);
                    }}>
                        Edit
                    </Button>
                    <Button variant="secondary" onClick={() => setShowBlogView(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ResearchNotes;
